import React, { useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import { useQuery, useQueryClient } from 'react-query';

import SlidePanel from '../../components/SlidePanel';
import VariantsSliderSelector from './VariantSliderSelector';
import { multiSelectType } from '../../components/MultiSelectDropdown';
import { getProductById, getProductsByHandle } from '../api';
import { useGlobalContext } from '../state/GlobalContext';
import useProductContext, { ProductContext } from '../state/ProductContext';
import { getCurrencyFormatter, getIdFromGid } from '../../utils';
import { ProductSwapTypes } from '../../utils/constants';
import {
  emptyGlobalProduct,
  emptyGlobalVariant,
} from '../../utils/globalObjects';
import { itemIsFreeProduct } from '../utils';
import { getGidFromIdForResource } from '../../../utils/gid-utils';
import uniqBy from 'lodash/uniqBy';

const CURRENCY = get(window, ['Shopify', 'currency', 'active'], 'USD');

export interface UpdateVariantProps {
  productId: string;
  currentVariantId: string;
  subscriptionCurrencyCode: string;
}

const UpdateVariant: React.FC<UpdateVariantProps> = ({
  productId,
  currentVariantId,
  subscriptionCurrencyCode,
}) => {
  const {
    image,
    product: line,
    isSlidePanelOpen,
    closeSlidePanel,
    updateVariant,
    openModal,
    isDecreaseDisabled,
    subscriptionContainsGift,
  } = useProductContext() as ProductContext;
  const {
    isLoading,
    product,
    selectedVariant,
    handleSelect,
    handleConfirm,
    isFreeProduct,
  } = useVariants(
    line,
    productId,
    isSlidePanelOpen,
    closeSlidePanel,
    updateVariant,
    currentVariantId,
    openModal,
    subscriptionCurrencyCode,
  );

  const hasVariants = product?.variants.length && product.variants.length > 1;
  const selectedImage = get(selectedVariant, 'image', image);
  const shouldHideVariantSelector = isLoading || !product || isFreeProduct;

  const content = (
    <SlidePanel
      open={isSlidePanelOpen}
      onClose={closeSlidePanel}
      imgUrl={selectedImage}
      imgs={product?.images}
      price={selectedVariant?.price as string}
      productName={product?.title}
      variantName={hasVariants ? selectedVariant?.name : ''}
      description={product?.descriptionHtml || product?.description}
      isLoading={!product || isEmpty(product)}
      hasOneVariant={product?.variants.length === 1}
      hidePrice={subscriptionContainsGift}
    >
      {shouldHideVariantSelector ? null : (
        <VariantsSliderSelector
          product={product}
          hasVariants={!!hasVariants}
          selectedVariant={selectedVariant}
          isDecreaseDisabled={isDecreaseDisabled}
          onSelect={handleSelect}
          onConfirm={handleConfirm}
          openModal={openModal}
        />
      )}
    </SlidePanel>
  );

  const elementId = 'slide-panel';
  let slidePanelDiv = document.getElementById(elementId);

  if (!slidePanelDiv) {
    slidePanelDiv = document.createElement('div');
    slidePanelDiv.id = elementId;
    document.body.appendChild(slidePanelDiv);
  }

  return ReactDOM.createPortal(content, slidePanelDiv);
};

export default UpdateVariant;

const useVariants = (
  line: Product,
  newProductId: string,
  isSlidePanelOpen: boolean,
  closeSlidePanel,
  updateVariant,
  currentVariantId: string,
  openModal: (productType: ProductSwapTypes) => void,
  subscriptionCurrencyCode: string,
) => {
  const productId = getIdFromGid(newProductId || line.ProductId);
  const isPrepaidV2 = !!line?.PrepaidV2;
  const { t } = useTranslation();

  const sellingPlanId = line?.SellingPlanId;

  const defaultVariantId = getIdFromGid(line.VariantId);
  const [variantId, setVariantId] = React.useState(
    getIdFromGid(currentVariantId),
  );
  const { customerId, subscriptionId, authToken, customerPortalSettings } =
    useGlobalContext();
  const queryClient = useQueryClient();
  const isFreeProduct = itemIsFreeProduct(
    line,
    customerPortalSettings?.preventEditFreeItems,
  );
  const toast = useToast();

  const {
    data: product,
    isLoading,
    isError,
    remove,
  } = useQuery(
    ['product', productId],
    async () => {
      const fetchedProduct = await getProductById(
        { customerId, subscriptionId, authToken },
        productId as string,
      );

      const currencyCode = fetchedProduct.currencyCode;

      if (currencyCode !== subscriptionCurrencyCode) {
        const { products, currencyCode } = await getProductsByHandle({
          customerId,
          subscriptionId,
          authToken,
          productsHandles: [fetchedProduct.handle],
        });
        if (currencyCode !== subscriptionCurrencyCode) {
          closeSlidePanel();
          toast({
            description: t('product_swap.empty'),
            status: 'error',
            isClosable: true,
            position: 'top',
            title: t('product_swap.error'),
          });
          return;
        }
        const product = get(products, '[0].productData', null);

        const apiProduct = liquidProductToApiProduct(
          product,
          subscriptionCurrencyCode,
        );

        const { formattedProduct, defaultVariant } = formatProduct(
          apiProduct,
          variantId ?? defaultVariantId,
          line.Quantity,
          isPrepaidV2,
          customerPortalSettings?.productChange === 'SAME_SUBSCRIPTION',
          sellingPlanId,
        );

        return {
          product: { ...formattedProduct, isPrepaidV2, sellingPlanId },
          variants: formattedProduct.variants,
          selectedVariant: defaultVariant,
        };
      }

      const { formattedProduct, defaultVariant } = formatProduct(
        fetchedProduct,
        variantId ?? defaultVariantId,
        line.Quantity,
        isPrepaidV2,
        customerPortalSettings?.productChange === 'SAME_SUBSCRIPTION',
        sellingPlanId,
      );

      return {
        product: { ...formattedProduct, isPrepaidV2, sellingPlanId },
        variants: formattedProduct.variants,
        selectedVariant: defaultVariant,
      };
    },
    { refetchOnWindowFocus: false, enabled: isSlidePanelOpen, retry: false },
  );

  useEffect(() => {
    if (isError && !isFreeProduct) {
      openModal(ProductSwapTypes.PRODUCT);
      remove();
      closeSlidePanel();
    }
  }, [closeSlidePanel, isError, openModal, remove]);

  const handleSelect = ({ id, quantity }: { id: string; quantity: number }) => {
    const selectedVariant = product?.variants.find(
      (variant) => variant?.id.toString() === id,
    );

    const productName = product?.product?.title;
    if (selectedVariant) {
      updateVariant(
        {
          ...selectedVariant,
          quantity,
          productName: productName || selectedVariant?.productName,
        },
        line.SubscriptionContractLineId,
      );
      closeSlidePanel();
    }
  };

  const handleConfirm = (selectedOption) => {
    const variant = product?.variants?.find(
      (v) => `${v.id}` == selectedOption.id,
    );

    if (variant) {
      setVariantId(getIdFromGid(variant?.id?.toString()) || '');
      queryClient.setQueryData(['product', productId], (old) => ({
        ...(old as GlobalProduct),
        selectedVariant: variant,
      }));
    } else {
      queryClient.setQueryData(['product', productId], (old) => ({
        ...(old as GlobalProduct),
        selectedVariant: undefined,
      }));
    }
  };

  return {
    isLoading,
    variants: product?.variants,
    product: product?.product,
    selectedVariant: product?.selectedVariant,
    handleConfirm,
    handleSelect,
    isFreeProduct,
  };
};

const filterAllowedOptions = (options, allowedVariants) =>
  options
    .map((o) => {
      const values = o.values.reduce((acc, value) => {
        const anyAllowedVariant = allowedVariants.some((variant) => {
          return variant.selectedOptions.map((o) => o.value).includes(value);
        });

        if (anyAllowedVariant) {
          acc.add(value);
        }

        return acc;
      }, new Set());

      const filteredValues = [...values];
      if (filteredValues.length === 0) {
        return null;
      }

      return {
        ...o,
        values: filteredValues,
      };
    })
    .filter(Boolean);

export const formatProduct = (
  product,
  variantId,
  quantity,
  isPrepaidV2,
  allowSwapFromSameSubscriptionOnly,
  sellingPlanId,
): {
  formattedProduct: GlobalProduct;
  defaultVariant: GlobalVariant;
} => {
  const image = get(product, ['featuredImage', 'transformedSrc'], '');
  let variants = product.variants.edges?.map((v) =>
    formatVariant(v, product, image),
  );

  const defaultVariant =
    variants.find((v) => getIdFromGid(v.id) === variantId) || variants[0];

  variants = variants.map((v) => ({
    ...v,
    available: isPrepaidV2
      ? v.available && v.price === defaultVariant.price
      : v.available,
  }));

  let allowedVariants = variants.filter((v) => {
    return isPrepaidV2 ? v.price === defaultVariant.price : true;
  });

  if (allowSwapFromSameSubscriptionOnly) {
    const currentVariant = allowedVariants.find(
      ({ id }) => id === defaultVariant.id,
    );
    const currentSpgGid = currentVariant?.sellingPlanGroups?.find((spg) =>
      spg.sellingPlans.map(({ gId }) => gId).includes(sellingPlanId),
    )?.gId;

    allowedVariants = currentSpgGid
      ? allowedVariants?.filter(({ sellingPlanGroups }) =>
          sellingPlanGroups.map(({ gId }) => gId).includes(currentSpgGid),
        )
      : allowedVariants;
  }

  const filteredOptions = filterAllowedOptions(
    product.options,
    allowedVariants,
  );

  let optionsAndValues;
  if (variants.length > 1) {
    optionsAndValues = filteredOptions.map(({ name, values, id }) => {
      const variantOptions = values.map((label) => ({
        label,
        id: label,
        type: 'option',
        optionName: name,
        isSelected: isOptionSelected(defaultVariant, { name, value: label }),
      }));
      return [{ label: name, id, type: 'title' }, ...variantOptions];
    });
  } else {
    optionsAndValues = [
      {
        label: product.title,
        id: `${product.variants.edges[0].node.id}`,
        type: 'option',
        isSelected: true,
      },
    ];
  }

  const variantsOptions = variants.map((v) => {
    const isSelected = `${v.id}` === defaultVariant.id;
    return transformVariantToMultiSelectOption(v, isSelected);
  });

  const productImages = isArray(product.images) ? product.images : [];

  const formattedProduct = {
    ...emptyGlobalProduct,
    id: product.id,
    description: product.description,
    descriptionHtml: product.descriptionHtml,
    image,
    variants,
    quantity,
    images: productImages.map(({ url }) => url),
    title: product.title,
    price: product.price,
    variantsOptions,
    options: [],
    optionsAndValues: flatten(optionsAndValues) as MultiSelectOption[],
  };
  return { formattedProduct, defaultVariant };
};

const formatVariant = ({ node }, product, imageSrc): GlobalVariant => {
  const currency = get(
    product,
    ['priceRangeV2', 'maxVariantPrice', 'currencyCode'],
    CURRENCY,
  );
  const format = getCurrencyFormatter(currency);
  const price = format(node.price);
  const id = node.id;
  const available = node.availableForSale;
  const productName = product.name;
  const selectedOptions = node.selectedOptions.map(({ name, value }) => ({
    name,
    value,
  }));

  const image = get(node, ['image', 'transformedSrc'], imageSrc);
  return {
    ...emptyGlobalVariant,
    id,
    price,
    productId: product.id,
    productName,
    description: node.description,
    descriptionHtml: node.descriptionHtml,
    title: node.title,
    name: node.title,
    available,
    image,
    options: node.options || [],
    selectedOptions,
    sellingPlanGroups: node.sellingPlanGroups,
    inventory: {
      inventoryManagement: node.inventoryManagement,
      inventoryQuantity: node.inventoryQuantity,
      inventoryPolicy: node.inventoryPolicy,
    },
  };
};

export const isOptionSelected = (
  selectedVariant: GlobalVariant,
  option: { name: string; value: string },
): boolean =>
  selectedVariant.selectedOptions?.some(
    ({ name, value }) => name === option.name && value === option.value,
  ) ?? false;

export const transformVariantToMultiSelectOption = (
  variant: GlobalVariant,
  isSelected = false,
): MultiSelectOption => ({
  label: variant.name,
  isSelected,
  id: `${variant.id}`,
  type: multiSelectType.OPTION,
});
const liquidProductToApiProduct = (
  product: LiquidProduct,
  subscriptionCurrencyCode: string,
) => {
  const newFetchedProduct = {
    description: product.description,
    descriptionHtml: product.description,
    featuredImage: { transformedSrc: product.featured_image },
    handle: product.handle,
    id: getGidFromIdForResource(product.id, 'Product'),
    images: product.images.map((img) => ({ url: img })),
    options: product.options.map((option, i) => ({
      name: option,
      values: product.variants.map((variant) => variant[`option${i + 1}`]),
    })),
    priceRangeV2: {
      maxVariantPrice: {
        amount: product.price_max / 100,
        currencyCode: subscriptionCurrencyCode,
      },
      minVariantPrice: {
        amount: product.price_min / 100,
        currencyCode: subscriptionCurrencyCode,
      },
    },
    status: 'ACTIVE',
    title: product.title,
    totalVariants: product.variants.length,
    variants: {
      edges: product.variants.map((variant) => {
        const uniqueSellingPlanGroups = uniqBy<LiquidSellingPlanAllocation>(
          variant.selling_plan_allocations,
          'selling_plan_group_id',
        );

        const sellingPlanGroups = uniqueSellingPlanGroups.map(
          (sellingPlanGroup) => ({
            gId: getGidFromIdForResource(
              sellingPlanGroup.selling_plan_group_id,
              'SellingPlanGroup',
            ),
            sellingPlans: variant.selling_plan_allocations
              .map(({ selling_plan_group_id }) => {
                if (
                  sellingPlanGroup.selling_plan_group_id ===
                  selling_plan_group_id
                ) {
                  return {
                    gId: getGidFromIdForResource(
                      sellingPlanGroup.selling_plan_id,
                      'SellingPlan',
                    ),
                  };
                }
              })
              .filter(Boolean),
          }),
        );

        return {
          node: {
            availableForSale: variant.available,
            displayName: variant.name,
            id: getGidFromIdForResource(variant.id, 'ProductVariant'),
            image: {
              transformedSrc: variant.featured_image?.src,
            },
            inventoryManagement: variant.inventory_management.toUpperCase(),
            price: +variant.price / 100,
            selectedOptions: product.options.map((option, i) => ({
              name: option,
              value: get(variant, `option${i + 1}`),
            })),
            sellingPlanGroups,
            title: variant.title,
          },
        };
      }),
    },
  };
  return newFetchedProduct;
};
